import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Snackbar, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon, Public as PublicIcon, LinkOff as RevokeIcon,
} from '@mui/icons-material';
import menuShareService from '../../services/menuShareService';
import type { MenuShare } from '../../types/menu';

interface Props {
  venueId: string;
  menuId: string;
  /** Menu.status — drives the DRAFT warning. */
  menuStatus: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
}

const PUBLIC_RENDER_NOTE =
  'The public render endpoint is not yet live for cocktail menus. The slug below is reserved but the customer-facing URL does not yet resolve.';

/**
 * Publish / share-link manager for a cocktail menu.
 *
 * Phase 2B.b2 deliberately does NOT render a live public URL — the public
 * `/m/:slug` route is wine-list-only today. Operators can still reserve a
 * slug + token now so the URL stays stable when the public render endpoint
 * lands for cocktails. No QR generation in this phase.
 */
const MenuPublishPanel: React.FC<Props> = ({ venueId, menuId, menuStatus }) => {
  const theme = useTheme();
  const [share, setShare] = useState<MenuShare | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    menuShareService.getShare(venueId, menuId)
      .then(setShare)
      .catch((err: any) => {
        // 404 → no active share yet. That's a normal state, not an error.
        if (err?.response?.status === 404) {
          setShare(null);
        } else {
          setError('Could not load publish status. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [venueId, menuId]);

  // Reserved (would-be) public URL. Shown only for operator reference; the
  // backend does not yet resolve this path for cocktail menus.
  const reservedUrl = share
    ? `${window.location.origin}/m/${encodeURIComponent(share.slug)}`
    : null;

  const handlePublish = async () => {
    setWorking(true);
    setError('');
    try {
      const created = await menuShareService.createOrGetShare(venueId, menuId);
      setShare(created);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not publish. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleRevoke = async () => {
    setWorking(true);
    setError('');
    try {
      await menuShareService.revokeShare(venueId, menuId);
      setShare(null);
      setConfirmRevoke(false);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not unpublish. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyToast(true);
    } catch {
      window.prompt('Copy the value:', value);
    }
  };

  const isDraft = menuStatus === 'DRAFT';

  return (
    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
          <PublicIcon sx={{ color: share ? 'success.main' : 'text.disabled', mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {share ? 'Public slug reserved' : 'Not published'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {share
                ? 'A slug is reserved for this menu. The customer-facing URL is not yet live (see notice below).'
                : 'Publish this menu to reserve a slug now. The customer-facing URL will start working when the public render endpoint ships for cocktail menus.'}
            </Typography>
          </Box>
        </Box>

        {!share && !loading && (
          <Button
            variant="contained"
            size="small"
            disabled={working}
            onClick={handlePublish}
            sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
          >
            {working ? <CircularProgress size={16} color="inherit" /> : 'Publish Menu'}
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mt: 1.5, borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      {/* Persistent warning — public render endpoint is not yet live. */}
      {share && (
        <Alert severity="warning" sx={{ mt: 2, borderRadius: 1 }}>
          {PUBLIC_RENDER_NOTE}
        </Alert>
      )}

      {/* DRAFT advisory — when the public render lands, DRAFT menus will 404
          even with a valid slug. Surface that now so operators know to flip
          status to ACTIVE before announcing the URL. */}
      {share && isDraft && (
        <Alert severity="info" sx={{ mt: 1.5, borderRadius: 1 }}>
          This menu is <strong>DRAFT</strong>. When the public route ships, DRAFT menus will
          return a "not available" page until set to <strong>ACTIVE</strong>.
        </Alert>
      )}

      {share && (
        <Box sx={{ mt: 2 }}>
          {/* Slug field */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Slug
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' }, mb: 1.5 }}>
            <TextField
              value={share.slug}
              size="small"
              fullWidth
              slotProps={{
                input: {
                  readOnly: true,
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    backgroundColor: alpha(theme.palette.text.primary, 0.03),
                  },
                },
              }}
              onFocus={e => e.target.select()}
            />
            <Tooltip title="Copy slug">
              <IconButton size="small" onClick={() => handleCopy(share.slug)} aria-label="Copy slug">
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Reserved (not-live) URL field — for operator reference only */}
          {reservedUrl && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Reserved URL (not yet live)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <TextField
                  value={reservedUrl}
                  size="small"
                  fullWidth
                  slotProps={{
                    input: {
                      readOnly: true,
                      sx: {
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        backgroundColor: alpha(theme.palette.text.primary, 0.03),
                        color: theme.palette.text.disabled,
                      },
                    },
                  }}
                  onFocus={e => e.target.select()}
                />
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Copy reserved URL">
                    <IconButton size="small" onClick={() => handleCopy(reservedUrl)} aria-label="Copy reserved URL">
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Unpublish">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={working}
                      onClick={() => setConfirmRevoke(true)}
                      aria-label="Unpublish"
                    >
                      <RevokeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Revoke confirmation */}
      <Dialog open={confirmRevoke} onClose={() => !working && setConfirmRevoke(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Unpublish menu?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The reserved slug will be marked inactive. The customer-facing URL is not live yet
            anyway, so no live link breaks — but re-publishing will issue a new URL.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRevoke(false)} disabled={working} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRevoke}
            disabled={working}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {working ? <CircularProgress size={18} color="inherit" /> : 'Unpublish'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copyToast}
        autoHideDuration={2000}
        onClose={() => setCopyToast(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default MenuPublishPanel;
